import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import RecipeCard from '../components/RecipeCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Search, Filter, X, ChefHat, Loader2, Sparkles, Plus, User, Users, DollarSign, Clock, Home } from 'lucide-react';
import { toast } from 'sonner';

export const RecipesPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    const [recipes, setRecipes] = useState([]);
    const [favorites, setFavorites] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    
    // Filters
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [category, setCategory] = useState(searchParams.get('category') || 'all');
    const [difficulty, setDifficulty] = useState(searchParams.get('difficulty') || 'all');
    const [maxCost, setMaxCost] = useState([5]);
    const [maxTime, setMaxTime] = useState([60]);
    const [soloFriendly, setSoloFriendly] = useState(searchParams.get('solo') === 'true');
    const [householdSize, setHouseholdSize] = useState(searchParams.get('household') || 'all');
    const [budgetType, setBudgetType] = useState(searchParams.get('budget') || 'all');

    const categories = [
        { value: 'all', label: 'Toutes', color: 'bg-gray-500' },
        { value: 'breakfast', label: 'Déjeuner', color: 'bg-yellow-500' },
        { value: 'lunch', label: 'Dîner', color: 'bg-green-500' },
        { value: 'dinner', label: 'Souper', color: 'bg-orange-500' },
        { value: 'soups', label: 'Soupes', color: 'bg-amber-500' },
        { value: 'appetizers', label: 'Entrées', color: 'bg-rose-500' },
        { value: 'salads', label: 'Salades', color: 'bg-lime-500' },
        { value: 'sides', label: 'Accompagnements', color: 'bg-cyan-500' },
        { value: 'dessert', label: 'Desserts', color: 'bg-pink-500' },
        { value: 'snack', label: 'Collations', color: 'bg-indigo-400' },
        { value: 'rescue', label: 'Recette secours', color: 'bg-red-500' },
        { value: 'base', label: 'Les bases', color: 'bg-purple-500' }
    ];

    const difficulties = [
        { value: 'all', label: 'Toutes' },
        { value: 'facile', label: 'Facile' },
        { value: 'moyen', label: 'Moyen' },
        { value: 'difficile', label: 'Difficile' }
    ];

    const householdSizes = [
        { value: 'all', label: 'Tous', icon: '👥', description: 'Toutes les recettes' },
        { value: 'solo', label: 'Solo', icon: '👤', description: '1 personne' },
        { value: 'couple', label: 'Couple', icon: '👫', description: '2 personnes' },
        { value: 'family', label: 'Famille', icon: '👨‍👩‍👧', description: '3-4 personnes' },
        { value: 'large_family', label: 'Grande famille', icon: '👨‍👩‍👧‍👦', description: '5+ personnes' }
    ];

    const budgetTypes = [
        { value: 'all', label: 'Tous', icon: '💰', description: 'Pas de limite', color: 'bg-gray-100' },
        { value: 'tight', label: 'Fin de mois', icon: '🏦', description: '≤ 2.50$/portion', color: 'bg-red-100' },
        { value: 'normal', label: 'Normal', icon: '💵', description: '≤ 5$/portion', color: 'bg-green-100' },
        { value: 'comfortable', label: 'Confortable', icon: '💎', description: 'Sans limite', color: 'bg-blue-100' }
    ];

    useEffect(() => {
        loadRecipes();
        if (user) {
            loadFavorites();
        }
    }, [searchParams, user]);

    const loadRecipes = async () => {
        setLoading(true);
        try {
            const params = {};
            if (searchParams.get('search')) params.search = searchParams.get('search');
            if (searchParams.get('category') && searchParams.get('category') !== 'all') {
                params.category = searchParams.get('category');
            }
            if (searchParams.get('difficulty') && searchParams.get('difficulty') !== 'all') {
                params.difficulty = searchParams.get('difficulty');
            }
            if (searchParams.get('max_cost')) params.max_cost = searchParams.get('max_cost');
            if (searchParams.get('max_time')) params.max_time = searchParams.get('max_time');
            if (searchParams.get('solo') === 'true') params.solo_friendly = true;
            if (searchParams.get('household') && searchParams.get('household') !== 'all') {
                params.household_size = searchParams.get('household');
            }
            if (searchParams.get('budget') && searchParams.get('budget') !== 'all') {
                params.budget_type = searchParams.get('budget');
            }
            
            const response = await api.getRecipes(params);
            setRecipes(response.data);
        } catch (error) {
            console.error('Error loading recipes:', error);
            toast.error('Erreur lors du chargement des recettes');
        } finally {
            setLoading(false);
        }
    };

    const loadFavorites = async () => {
        try {
            const response = await api.getFavorites();
            setFavorites(new Set(response.data.map(r => r.id)));
        } catch (error) {
            console.error('Error loading favorites:', error);
        }
    };

    const applyFilters = () => {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (category !== 'all') params.set('category', category);
        if (difficulty !== 'all') params.set('difficulty', difficulty);
        if (maxCost[0] < 5) params.set('max_cost', maxCost[0].toString());
        if (maxTime[0] < 60) params.set('max_time', maxTime[0].toString());
        if (soloFriendly) params.set('solo', 'true');
        if (householdSize !== 'all') params.set('household', householdSize);
        if (budgetType !== 'all') params.set('budget', budgetType);
        
        setSearchParams(params);
        setShowFilters(false);
    };

    const clearFilters = () => {
        setSearch('');
        setCategory('all');
        setDifficulty('all');
        setMaxCost([5]);
        setMaxTime([60]);
        setSoloFriendly(false);
        setHouseholdSize('all');
        setBudgetType('all');
        setSearchParams({});
    };

    const handleFavoriteToggle = async (recipeId) => {
        if (!user) {
            navigate('/login');
            return;
        }
        
        try {
            if (favorites.has(recipeId)) {
                await api.removeFavorite(recipeId);
                setFavorites(prev => {
                    const next = new Set(prev);
                    next.delete(recipeId);
                    return next;
                });
                toast.success('Retiré des favoris');
            } else {
                await api.addFavorite(recipeId);
                setFavorites(prev => new Set([...prev, recipeId]));
                toast.success('Ajouté aux favoris');
            }
        } catch (error) {
            toast.error('Erreur lors de la mise à jour des favoris');
        }
    };

    const hasActiveFilters = search || category !== 'all' || difficulty !== 'all' || maxCost[0] < 5 || maxTime[0] < 60 || householdSize !== 'all' || budgetType !== 'all';

    return (
        <div className="min-h-screen py-8 md:py-12 relative overflow-hidden">
            {/* Decorative blobs */}
            <div className="blob-green top-20 -right-40 opacity-20" />
            <div className="blob-purple bottom-40 -left-40 opacity-20" />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Header */}
                <div className="mb-10">
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border-2 border-foreground shadow-pop font-heading font-bold text-sm mb-4">
                        <ChefHat className="w-4 h-4 text-primary" />
                        {recipes.length} recettes disponibles
                    </span>
                    <h1 className="font-heading text-5xl md:text-6xl font-black text-foreground mb-4">
                        Toutes les <span className="text-gradient-cameleon">recettes</span>
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-xl">
                        Découvrez notre collection de recettes économiques et délicieuses
                    </p>
                    
                    {/* Submit Button */}
                    <Button
                        onClick={() => user ? navigate('/recipes/new') : navigate('/login')}
                        className="mt-4 btn-pop bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full px-6 py-5 font-bold"
                        data-testid="submit-recipe-btn"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Proposer une recette
                    </Button>
                </div>

                {/* Category Pills */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {categories.map((cat, index) => (
                        <button
                            key={cat.value}
                            onClick={() => {
                                setCategory(cat.value);
                                const params = new URLSearchParams(searchParams);
                                if (cat.value === 'all') {
                                    params.delete('category');
                                } else {
                                    params.set('category', cat.value);
                                }
                                setSearchParams(params);
                            }}
                            className={`category-pill ${category === cat.value ? 'active' : ''}`}
                        >
                            {cat.label}
                        </button>
                    ))}
                </div>

                {/* Solo Filter Toggle */}
                <div className="mb-8">
                    <button
                        onClick={() => {
                            const newValue = !soloFriendly;
                            setSoloFriendly(newValue);
                            const params = new URLSearchParams(searchParams);
                            if (newValue) {
                                params.set('solo', 'true');
                            } else {
                                params.delete('solo');
                            }
                            setSearchParams(params);
                        }}
                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all ${
                            soloFriendly 
                                ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-200' 
                                : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        }`}
                        data-testid="solo-filter-btn"
                    >
                        <User className="w-4 h-4" />
                        Cuisiner en solo
                        {soloFriendly && (
                            <span className="ml-1 w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-xs">✓</span>
                        )}
                    </button>
                    {soloFriendly && (
                        <span className="ml-3 text-sm text-orange-600">
                            Recettes avec conseils pour personnes seules
                        </span>
                    )}
                </div>

                {/* Search & Filters Bar */}
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                    <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder="Rechercher une recette..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                            className="pl-12 py-6 rounded-full border-2 border-foreground bg-white text-lg font-medium shadow-pop focus:shadow-pop-hover transition-all"
                            data-testid="recipes-search-input"
                        />
                    </div>
                    
                    <div className="flex gap-3">
                        <Button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`btn-pop ${showFilters ? 'bg-purple-500 text-white' : 'bg-white text-foreground'}`}
                            data-testid="toggle-filters-btn"
                        >
                            <Filter className="w-5 h-5 mr-2" />
                            Filtres
                            {hasActiveFilters && (
                                <span className="ml-2 w-3 h-3 bg-secondary rounded-full" />
                            )}
                        </Button>
                        
                        <Button
                            onClick={applyFilters}
                            className="btn-pop bg-primary text-white"
                            data-testid="apply-search-btn"
                        >
                            <Search className="w-5 h-5" />
                        </Button>
                    </div>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="card-pop p-6 mb-8 animate-fade-in">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-heading font-bold text-xl">🎯 Trouvez LA recette parfaite</h3>
                            {hasActiveFilters && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={clearFilters}
                                    className="text-muted-foreground font-bold"
                                    data-testid="clear-filters-btn"
                                >
                                    <X className="w-4 h-4 mr-1" />
                                    Effacer
                                </Button>
                            )}
                        </div>
                        
                        {/* Quick Needs Section */}
                        <div className="mb-6 p-4 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl border-2 border-primary/20">
                            <h4 className="font-bold text-foreground mb-3 flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-primary" />
                                Vos besoins du moment
                            </h4>
                            
                            {/* Budget Type */}
                            <div className="mb-4">
                                <label className="text-sm font-medium text-muted-foreground mb-2 block">💰 Situation budget</label>
                                <div className="flex flex-wrap gap-2">
                                    {budgetTypes.map(budget => (
                                        <button
                                            key={budget.value}
                                            onClick={() => setBudgetType(budget.value)}
                                            className={`px-4 py-2 rounded-xl border-2 transition-all font-medium text-sm ${
                                                budgetType === budget.value
                                                    ? 'border-primary bg-primary text-white shadow-lg scale-105'
                                                    : `border-foreground/20 ${budget.color} hover:border-primary/50`
                                            }`}
                                            data-testid={`budget-${budget.value}`}
                                        >
                                            <span className="mr-1">{budget.icon}</span>
                                            {budget.label}
                                            <span className="block text-xs opacity-75">{budget.description}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Household Size */}
                            <div>
                                <label className="text-sm font-medium text-muted-foreground mb-2 block">👥 Vous cuisinez pour</label>
                                <div className="flex flex-wrap gap-2">
                                    {householdSizes.map(size => (
                                        <button
                                            key={size.value}
                                            onClick={() => setHouseholdSize(size.value)}
                                            className={`px-4 py-2 rounded-xl border-2 transition-all font-medium text-sm ${
                                                householdSize === size.value
                                                    ? 'border-secondary bg-secondary text-white shadow-lg scale-105'
                                                    : 'border-foreground/20 bg-white hover:border-secondary/50'
                                            }`}
                                            data-testid={`household-${size.value}`}
                                        >
                                            <span className="mr-1">{size.icon}</span>
                                            {size.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        {/* Classic Filters */}
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {/* Category */}
                            <div>
                                <label className="text-sm font-bold text-foreground mb-2 block">
                                    🍽️ Catégorie
                                </label>
                                <Select value={category} onValueChange={setCategory}>
                                    <SelectTrigger className="rounded-xl border-2 border-foreground" data-testid="category-select">
                                        <SelectValue placeholder="Choisir..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map(cat => (
                                            <SelectItem key={cat.value} value={cat.value}>
                                                {cat.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            {/* Difficulty */}
                            <div>
                                <label className="text-sm font-bold text-foreground mb-2 block">
                                    📊 Difficulté
                                </label>
                                <Select value={difficulty} onValueChange={setDifficulty}>
                                    <SelectTrigger className="rounded-xl border-2 border-foreground" data-testid="difficulty-select">
                                        <SelectValue placeholder="Choisir..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {difficulties.map(diff => (
                                            <SelectItem key={diff.value} value={diff.value}>
                                                {diff.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            {/* Max Cost */}
                            <div>
                                <label className="text-sm font-bold text-foreground mb-2 block">
                                    💵 Budget max: <span className="text-primary">{maxCost[0]}$/portion</span>
                                </label>
                                <Slider
                                    value={maxCost}
                                    onValueChange={setMaxCost}
                                    max={10}
                                    min={0.5}
                                    step={0.5}
                                    className="mt-4"
                                    data-testid="cost-slider"
                                />
                            </div>
                            
                            {/* Max Time */}
                            <div>
                                <label className="text-sm font-bold text-foreground mb-2 block">
                                    ⏱️ Temps max: <span className="text-secondary">{maxTime[0]} min</span>
                                </label>
                                <Slider
                                    value={maxTime}
                                    onValueChange={setMaxTime}
                                    max={120}
                                    min={10}
                                    step={10}
                                    className="mt-4"
                                    data-testid="time-slider"
                                />
                            </div>
                        </div>
                        
                        <div className="flex justify-end mt-6">
                            <Button
                                onClick={applyFilters}
                                className="btn-pop bg-primary text-white"
                                data-testid="apply-filters-btn"
                            >
                                Appliquer les filtres
                            </Button>
                        </div>
                    </div>
                )}

                {/* Results */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="text-center">
                            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                            <p className="text-muted-foreground font-medium">Chargement des recettes...</p>
                        </div>
                    </div>
                ) : recipes.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-foreground shadow-pop">
                            <ChefHat className="w-12 h-12 text-muted-foreground" />
                        </div>
                        <h3 className="font-heading font-bold text-2xl text-foreground mb-2">
                            Aucune recette trouvée
                        </h3>
                        <p className="text-muted-foreground mb-6">
                            Essayez de modifier vos critères de recherche
                        </p>
                        <Button
                            onClick={clearFilters}
                            className="btn-pop bg-white text-foreground"
                        >
                            Réinitialiser les filtres
                        </Button>
                    </div>
                ) : (
                    <>
                        <p className="text-muted-foreground mb-6 font-medium">
                            <span className="text-foreground font-bold">{recipes.length}</span> recette{recipes.length > 1 ? 's' : ''} trouvée{recipes.length > 1 ? 's' : ''}
                        </p>
                        
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                            {recipes.map((recipe, index) => (
                                <div key={recipe.id} className={`animate-fade-in stagger-${(index % 5) + 1}`}>
                                    <RecipeCard
                                        recipe={recipe}
                                        isFavorite={favorites.has(recipe.id)}
                                        onFavoriteToggle={handleFavoriteToggle}
                                    />
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default RecipesPage;
